import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
    IEmployeeOrder,
    IInvitationsQueryVariables,
    useInvitationsQuery,
    useInvitationsCancelMutation,
} from 'graphql/types';
import { Table } from 'common/components/Table';
import { THeadCell, TTableRow } from 'common/types/table';
import { getDisplayDate, getIntlPath } from 'common/utils';
import { useSnackbarContext } from 'common/context';

interface IProps {
    disableOrderBy?: boolean;
    editable?: boolean;
    queryVariables: IInvitationsQueryVariables;
    onQueryCountChange?(count: number): void;
    onQueryVariablesChange?(variables: IInvitationsQueryVariables): void;
}

export const InvitationTable = ({
    disableOrderBy,
    editable,
    queryVariables,
    onQueryCountChange,
    onQueryVariablesChange,
}: IProps) => {
    const { t } = useTranslation();
    const history = useHistory();
    const { setSnackbarState } = useSnackbarContext();
    const { data, loading, refetch } = useInvitationsQuery({
        variables: queryVariables,
        onError: (error) => {
            setSnackbarState({
                message: error.message,
                variant: 'error',
            });
        },
        fetchPolicy: 'no-cache',
    });
    const [cancelInvitations] = useInvitationsCancelMutation({
        onCompleted: () => {
            refetch({ ...queryVariables, offset: 0 });

            setSnackbarState({
                message: t('messages.invitationsCancelled'),
                variant: 'success',
            });
        },
        onError: (error) => {
            setSnackbarState({
                message: error.message,
                variant: 'error',
            });
        },
    });

    const { invitations } = data || {};

    useEffect(() => {
        if (invitations && onQueryCountChange) {
            onQueryCountChange(invitations.count);
        }
    }, [invitations?.count]);

    const tableHeadCells: THeadCell[] = [
        {
            id: IEmployeeOrder.FirstName,
            label: t('employee.table.firstName'),
        },
        {
            id: IEmployeeOrder.LastName,
            label: t('employee.table.lastName'),
        },
        {
            id: IEmployeeOrder.Status,
            label: t('employee.table.budget'),
        },
        {
            id: IEmployeeOrder.Budget,
            label: t('employee.status'),
        },
        {
            id: IEmployeeOrder.UpdatedAt,
            label: t('employee.table.updatedAt'),
            default: true,
            defaultOrder: 'desc',
        },
    ];

    let tableRows: TTableRow[] = [];

    if (invitations && invitations.objects) {
        tableRows = invitations.objects.map((invitation) => ({
            id: invitation.id,
            employeeId: invitation.employee.id,
            cells: [
                { label: invitation.employee.firstName },
                {
                    label: `${invitation.employee.lastNamePrefix} ${invitation.employee.lastName}`,
                },
                { label: invitation.budget.name },
                { label: invitation.statusDisplay },
                { label: getDisplayDate(invitation.updatedAt) },
            ],
        }));
    }

    const handleInvitationCancel = (employeeIds: string[]) => {
        cancelInvitations({
            variables: {
                ids: employeeIds,
            },
        });
    };

    const handleRowClick = (employeeId: string) => {
        history.push(`${getIntlPath('employees')}/${employeeId}`);
    };

    const handleSort = (order: string, headCellId: IEmployeeOrder) => {
        if (!onQueryVariablesChange) return;

        const newVariables = {
            ...queryVariables,
            orderBy: headCellId,
            ascending: order === 'asc',
        };

        onQueryVariablesChange(newVariables);
    };

    const handleChangePage = (page: number, rowsPerPage: number) => {
        if (!onQueryVariablesChange) return;

        const newVariables = {
            ...queryVariables,
            offset: page * rowsPerPage,
        };

        onQueryVariablesChange(newVariables);
    };

    const handleChangeRowsPerPage = (rowsPerPage: number) => {
        if (!onQueryVariablesChange) return;

        const newVariables = {
            ...queryVariables,
            limit: rowsPerPage,
        };

        onQueryVariablesChange(newVariables);
    };

    return (
        <Table
            disableOrderBy={disableOrderBy}
            editable={editable}
            loading={loading}
            tableHeadCells={tableHeadCells}
            tableRows={tableRows}
            tableSettings={{
                rowsPerPage: queryVariables.limit || 25,
            }}
            totalCount={invitations?.count || 0}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            onInvitationCancel={handleInvitationCancel}
            onRowClick={handleRowClick}
            onSort={handleSort}
        />
    );
};
